import RootState from '../../rootState';
import Provider, { ProviderInfoPayment, ProviderInformationRequest } from '../../../models/provider';
import Facility from 'src/models/facility';

export class Action {
    type: string;
    providerInfo: Provider;
    facilities: Array<Facility>;
    tenantName: string;

    emailIsValid: boolean;

    registrationId: string;
    providerSummary: Provider;
    statusCode?: number;
    errorMessage: string;

    providerInformation: ProviderInformationRequest;
}

export interface State {
    saving: boolean;
    saved: boolean;

    emailIsValid: boolean;

    provider: ProviderInfoPayment;
    facilities: Array<Facility>;
    loadingFacility: boolean;

    //summary
    loading: boolean;
    providerSummary: Provider;

    submitting: boolean;
    submitted: boolean;

    // error
    statusCode?: number;
    errorMessage: string;

    providerInformation: ProviderInfoPayment;
}

export const types = {
    ACTION_SERVER_DATA_PROVIDER_REQUESTED: 'ACTION_SERVER_DATA_PROVIDER_REQUESTED',
    ACTION_SERVER_DATA_PROVIDER_FAILED: 'ACTION_SERVER_DATA_PROVIDER_FAILED',

    ADD_PROVIDER_ASYNC: 'ADD_PROVIDER_ASYNC',
    ADD_PROVIDER_SUCCEED: 'ADD_PROVIDER_SUCCEED',
    ADD_PROVIDER_FAILED: 'ADD_PROVIDER_FAILED',

    EMAIL_IS_VALID: 'EMAIL_IS_VALID',

    STORE_PROVIDER_INFO_PAYMENT: 'STORE_PROVIDER_INFO_PAYMENT',

    GET_PROVIDER_INFO_ASYNC: 'GET_PROVIDER_INFO_ASYNC',
    GET_PROVIDER_INFO_SUCCEEDED: 'GET_PROVIDER_INFO_SUCCEEDED',

    GET_FACILITIES_BY_TENANT_NAME_ASYNC: 'GET_FACILITIES_BY_TENANT_NAME_ASYNC',
    GET_FACILITIES_BY_TENANT_NAME_SUCCEEDED: 'GET_FACILITIES_BY_TENANT_NAME_SUCCEEDED',
    GET_FACILITIES_BY_TENANT_NAME_FAILED: 'GET_FACILITIES_BY_TENANT_NAME_FAILED',

    CLEAR_NEARBY_FACILITIES: 'CLEAR_NEARBY_FACILITIES',

    SUBMIT_TO_VENDOR_ASYNC: 'SUBMIT_TO_VENDOR_ASYNC',
    SUBMIT_TO_VENDOR_SUCCEED: 'SUBMIT_TO_VENDOR_SUCCEED',
    SUBMIT_TO_VENDOR_FAILED: 'SUBMIT_TO_VENDOR_FAILED',
    RESET_SUBMIT_STATUS: 'RESET_SUBMIT_STATUS',
    UPDATE_STATUS_CODE: 'UPDATE_STATUS_CODE',

    ADD_PROVIDER_INFORMATION_ASYNC: 'ADD_PROVIDER_INFORMATION_ASYNC',
    ADD_PROVIDER_INFORMATION_SUCCEED: 'ADD_PROVIDER_INFORMATION_SUCCEED',
    ADD_PROVIDER_INFORMATION_FAILED: 'ADD_PROVIDER_INFORMATION_FAILED',

    STORE_PROVIDER_INFORMATION_PAYMENT: 'STORE_PROVIDER_INFORMATION_PAYMENT',
};

const baseAction = new Action();

export const actions = {
    actionServerDataProviderRequested: (): Action => ({
        ...baseAction,
        type: types.ACTION_SERVER_DATA_PROVIDER_REQUESTED
    }),

    actionServerDataProviderFailed: (): Action => ({
        ...baseAction,
        type: types.ACTION_SERVER_DATA_PROVIDER_FAILED
    }),

    addProviderAsync: (providerInfo: Provider): Action => ({
        ...baseAction,
        type: types.ADD_PROVIDER_ASYNC,
        providerInfo
    }),

    addProviderSucceeded: (): Action => ({
        ...baseAction,
        type: types.ADD_PROVIDER_SUCCEED
    }),

    addProviderFailed: (errorMessage: string): Action => ({
        ...baseAction,
        type: types.ADD_PROVIDER_FAILED,
        errorMessage: errorMessage
    }),

    emailIsValid: (emailIsValid: boolean): Action => ({
        ...baseAction,
        type: types.EMAIL_IS_VALID,
        emailIsValid
    }),

    storeProviderInfoPayment: (providerInfo: Provider): Action => ({
        ...baseAction,
        type: types.STORE_PROVIDER_INFO_PAYMENT,
        providerInfo
    }),

    getProviderInfoAsync: (registrationId: string): Action => ({
        ...baseAction,
        type: types.GET_PROVIDER_INFO_ASYNC,
        registrationId
    }),

    getProviderInfoSucceeded: (providerSummary: Provider): Action => ({
        ...baseAction,
        type: types.GET_PROVIDER_INFO_SUCCEEDED,
        providerSummary
    }),

    getFacilitiesByTenantNameAsync: (tenantName: string): Action => ({
        ...baseAction,
        type: types.GET_FACILITIES_BY_TENANT_NAME_ASYNC,
        tenantName,
    }),

    getFacilitiesByTenantNameSucceeded: (facilities: Facility[]): Action => ({
        ...baseAction,
        type: types.GET_FACILITIES_BY_TENANT_NAME_SUCCEEDED,
        facilities,
    }),

    getFacilitiesByTenantNameFailed: (): Action => ({
        ...baseAction,
        type: types.GET_FACILITIES_BY_TENANT_NAME_FAILED,
    }),

    submitToVendorAsync: (registrationId: string): Action => ({
        ...baseAction,
        type: types.SUBMIT_TO_VENDOR_ASYNC,
        registrationId
    }),

    submitToVendorSucceeded: (): Action => ({
        ...baseAction,
        type: types.SUBMIT_TO_VENDOR_SUCCEED
    }),

    submitToVendorFailed: (): Action => ({
        ...baseAction,
        type: types.SUBMIT_TO_VENDOR_FAILED
    }),

    resetSubmitStatus: (): Action => ({
        ...baseAction,
        type: types.RESET_SUBMIT_STATUS
    }),

    updateStatusCode: (statusCode?: number): Action => ({
        ...baseAction,
        type: types.UPDATE_STATUS_CODE,
        statusCode
    }),
    addProviderInformationAsync: (providerInformation: ProviderInformationRequest): Action => ({
        ...baseAction,
        type: types.ADD_PROVIDER_INFORMATION_ASYNC,
        providerInformation
    }),

    addProviderInformationSucceeded: (): Action => ({
        ...baseAction,
        type: types.ADD_PROVIDER_INFORMATION_SUCCEED
    }),

    addProviderInformationFailed: (errorMessage: string): Action => ({
        ...baseAction,
        type: types.ADD_PROVIDER_INFORMATION_FAILED,
        errorMessage: errorMessage
    }),

    storeProviderInformationPayment: (providerInformation: ProviderInformationRequest): Action => ({
        ...baseAction,
        type: types.STORE_PROVIDER_INFORMATION_PAYMENT,
        providerInformation
    }),
};

export const selectors = {
    getState: (rootState: RootState): State => rootState.entities.providerData,

    emailIsValid(rootState: RootState): boolean {
        const { emailIsValid } = this.getState(rootState);
        return emailIsValid;
    },

    saving(rootState: RootState): boolean {
        const { saving } = this.getState(rootState);
        return saving;
    },

    saved(rootState: RootState): boolean {
        const { saved } = this.getState(rootState);
        return saved;
    },

    getProviderInfoPayment(rootState: RootState): ProviderInfoPayment {
        const { provider } = this.getState(rootState);
        return provider;
    },

    loading(rootState: RootState): boolean {
        const { loading } = this.getState(rootState);
        return loading;
    },

    getProviderInfoSummary(rootState: RootState): Provider {
        const { providerSummary } = this.getState(rootState);
        return providerSummary;
    },

    submitting(rootState: RootState): boolean {
        const { submitting } = this.getState(rootState);
        return submitting;
    },

    submitted(rootState: RootState): boolean {
        const { submitted } = this.getState(rootState);
        return submitted;
    },

    getStatusCode(rootState: RootState): number {
        const { statusCode } = this.getState(rootState);
        return statusCode ? statusCode : -1;
    },

    getErrorMessage(rootState: RootState): string {
        const { errorMessage } = this.getState(rootState);
        return errorMessage;
    },

    getFacilities(rootState: RootState): Array<Facility> {
        const { facilities } = this.getState(rootState);
        return facilities;
    },

    getLoadingFacilities(rootState: RootState): boolean {
        const { loadingFacility } = this.getState(rootState);
        return loadingFacility;
    },

    getProviderInformationPayment(rootState: RootState): ProviderInfoPayment {
        const { providerInformation } = this.getState(rootState);
        return providerInformation;
    },
};


// WEBPACK FOOTER //
// ./src/modules/entities/provider/index.ts